import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PlanningList,
  CreatePlanningListInput,
  UpdatePlanningListInput,
  ClonePlanningListInput,
  SavePlanningListAsTemplateInput,
  CreatePlanningItemInput,
  UpdatePlanningItemInput,
  CreateItemTaskInput,
  ChecklistItem,
} from '@peles/shared';
import * as planningApi from '../api/planning';

// ─── Planning Lists ───────────────────────────────────────────────────────────

export function usePlanningLists(filters?: {
  eventId?: string;
  isTemplate?: boolean;
  eventTypeId?: string;
}) {
  return useQuery<PlanningList[]>({
    queryKey: ['planning-lists', filters ?? 'all'],
    queryFn: () => planningApi.getPlanningLists(filters),
  });
}

export function usePlanningList(id: string) {
  return useQuery<PlanningList>({
    queryKey: ['planning-lists', 'detail', id],
    queryFn: () => planningApi.getPlanningList(id),
    enabled: !!id,
  });
}

export function usePlanningTemplates(filters?: {
  eventTypeId?: string;
  includeArchived?: boolean;
}) {
  return useQuery<PlanningList[]>({
    queryKey: ['planning-templates', filters ?? 'all'],
    queryFn: () => planningApi.getPlanningTemplates(filters),
  });
}

export function useCreatePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePlanningListInput) => planningApi.createPlanningList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

export function useUpdatePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdatePlanningListInput }) =>
      planningApi.updatePlanningList(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

export function useDeletePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => planningApi.deletePlanningList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

export function useClonePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ClonePlanningListInput & { id: string }) =>
      planningApi.clonePlanningList(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useSavePlanningListAsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SavePlanningListAsTemplateInput & { id: string }) =>
      planningApi.savePlanningListAsTemplate(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

export function useArchivePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => planningApi.archivePlanningList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

// ─── Planning Items ───────────────────────────────────────────────────────────

export function useAddPlanningItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, ...input }: CreatePlanningItemInput & { listId: string }) =>
      planningApi.addPlanningItem(listId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useUpdatePlanningItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, updates }: { itemId: string; updates: UpdatePlanningItemInput }) =>
      planningApi.updatePlanningItem(itemId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useDeletePlanningItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => planningApi.deletePlanningItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

// ─── An event's one list ─────────────────────────────────────────────────────

export function useEventPlanningList(eventId: string | undefined) {
  return useQuery({
    queryKey: ['planning-lists', 'event', eventId],
    queryFn: () => planningApi.getEventPlanningList(eventId!),
    enabled: !!eventId,
  });
}

/** Items onto the event's list: one, a paste or a duplicate, all or none. */
export function useAddEventItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, items }: { eventId: string; items: CreatePlanningItemInput[] }) =>
      planningApi.addEventItems(eventId, items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useReorderEventItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, itemIds }: { eventId: string; itemIds: string[] }) =>
      planningApi.reorderEventItems(eventId, itemIds),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

// ─── An item's tasks ──────────────────────────────────────────────────────────

export function useItemTasks(itemId: string | undefined) {
  return useQuery<ChecklistItem[]>({
    queryKey: ['item-tasks', itemId],
    queryFn: () => planningApi.getItemTasks(itemId!),
    enabled: !!itemId,
  });
}

/** A task about an item; it lands on the event's Tasks list too. */
export function useAddItemTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, ...input }: CreateItemTaskInput & { itemId: string }) =>
      planningApi.addItemTask(itemId, input),
    onSuccess: (_task, { itemId }) => {
      queryClient.invalidateQueries({ queryKey: ['item-tasks', itemId] });
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
